import { useState } from 'react';

import { consoleDev, ConsoleStatus, fetchData } from '@quizzach/utils';
import { ICart } from '@quizzach/interfaces';

import { generateMockCart } from './cart.mock';
import { MOCK_CART } from './mocks.config';

export const useCart = () => {
	const [error, setError] = useState<boolean>(false);
	const [cart, setCart] = useState<ICart>();
	const [loading, setLoading] = useState<boolean>(true);
	const [deleteInProgress, setDeleteInProgress] = useState<boolean>(false);

	const retrieveCart = async (preventLoading?: boolean | undefined) => {
		// needed to avoid rerender right side of generator when deleting items because we're fetching again
		if (preventLoading === true) {
			setLoading(false);
		} else setLoading(true);

		if (MOCK_CART) {
			setCart(generateMockCart());
			setLoading(false);
			return;
		}

		try {
			const response = await fetchData<ICart>(
				`/api/cart`,
				{ method: 'GET' },
				{
					authRoute: true,
				}
			);
			if (response.status >= 300) {
				consoleDev('retrieveUserCart', ConsoleStatus.HTTP, response.status);
				setError(true);
			} else {
				consoleDev('retrieveUserCart', ConsoleStatus.SUCC);

				if (response.data) {
					const sortedCart = {
						...response.data,
						cart_items: [...response.data.cart_items].sort(
							(a, b) => +b.created_at - +a.created_at
						),
					};
					setCart(sortedCart);
				}
			}
		} catch (err) {
			consoleDev('retrieveUserCart', ConsoleStatus.NET, err);
			setError(true);
		} finally {
			setLoading(false);
		}
	};

	const deleteCartItem = async (uuid: string) => {
		setDeleteInProgress(true);
		if (MOCK_CART) {
			// local removal only, so deletions stay testable against the mock
			setCart((prev) =>
				prev
					? {
							...prev,
							cart_items: prev.cart_items.filter((item) => item.uuid !== uuid),
						}
					: prev
			);
			setDeleteInProgress(false);
			return;
		}
		try {
			const response = await fetchData<Record<string, never>>(
				`/api/cart/items/${uuid}`,
				{
					method: 'DELETE',
				},
				{
					authRoute: true,
				}
			);
			if (response.status >= 300) {
				consoleDev('deleteCartItem', ConsoleStatus.HTTP, response.status);
				setError(true);
			} else {
				consoleDev('deleteCartItem', ConsoleStatus.SUCC);
				await retrieveCart(true);
			}
		} catch (err) {
			consoleDev('deleteCartItem', ConsoleStatus.NET, err);
			setError(true);
		} finally {
			setDeleteInProgress(false);
		}
	};

	return {
		cart,
		error,
		loading,
		deleteInProgress,
		deleteCartItem,
		retrieveCart,
	};
};
